/**
 * NS-1 — 상세 `<title>` 꼬리를 **단계로** 고른다 (2026-09-21 · 회부_NS §1).
 *
 * ── 무엇이 고장이었나 ───────────────────────────────────────────────────────
 * 꼬리는 `site_type` 으로 정해지고 있었다. 그런데 `site_type` 은 «영구 불변» 이다 —
 * 청약을 거친 현장은 입주가 끝나도 `subscription` 이라, 제목이 끝난 청약을 계속 약속했다.
 *
 *   실측 2026-09-21 (active 6,352):
 *     site_type='subscription' 2,826곳 중 **2,737곳(96.9%)이 청약이 끝난 단계**
 *     (construction · move_in_ready · move_in_started · post_move_in · landmark_active)
 *     그 전부가 「분양정보 · 청약일정」을 제목에 달고 있었다.
 *
 * `lifecycle_stage` 는 매일 `stage-derive` 가 갱신한다 — 「지금 무슨 국면인가」를 아는 유일한 축이다.
 *
 * ── 문안 원칙 (판정회신_NS-1 §2) ────────────────────────────────────────────
 * **그 단계에서 DB 가 «확정으로» 가진 정보 축 2개만 약속한다.** 지어내지 않는다.
 * 어휘는 전부 기존 것의 재사용이고, 새 판정식을 만들지 않는다(축은 lifecycle_stage 하나).
 *
 * ⛔ 「선착순」을 `unsold_active` 밖에서 쓰지 않는다. 실측상 그 밖에 근거가 «없다» —
 *    `remaining_units > 0` 0곳 · `discount_pct` 보유 0곳. 단계 하나가 유일한 증거다.
 *    데이터가 말하지 않은 국면을 제목이 말하면 그건 V17·§7-1 이 막아 온 바로 그 거짓이다.
 * ⚠️ 꼬리를 늘리려면 이 표에 줄을 더하는 것이 아니라 «그 단계의 문안을 판정» 하는 것이 먼저다.
 */

/* ⚠️ 이 파일은 «잎» 이다 — `detail-derived.ts` 가 여기를 재수출한다(그쪽 L295).
      거꾸로 import 하면 순환이므로, 표기 도우미는 더 아래 잎에서만 가져온다.
        `@/lib/format`        → import 0개
        `@/lib/apt/schedule`  → sale-period → sale-source (여기로 돌아오지 않는다) */
import { fmtAmount } from '@/lib/format';
import { fmtPriceFrom } from '@/lib/fmt';
import { ymText } from '@/lib/apt/schedule';

/** `site_type` 기반 현행 표. 단계가 말이 없을 때 쓰는 바닥이다. */
export const SITE_TYPE_TAIL: Record<string, string> = {
  subscription: '분양정보 · 청약일정',
  redevelopment: '재개발 · 진행현황',
  unsold: '미분양 현황',
  trade: '실거래가 · 시세',
  landmark: '시세 · 분석',
};

/**
 * 「잔여세대 · 선착순」 — 단계 하나가 유일한 증거라 **site_type 을 가로질러** 이긴다.
 * ⛔ 이 어휘를 다른 단계로 넓히지 않는다. 실측: `remaining_units > 0` 0곳 ·
 *    `discount_pct` 보유 0곳 — `lifecycle_stage='unsold_active'`(192곳) 밖에 근거가 없다.
 */
const UNSOLD_TAIL = '잔여세대 · 선착순';

/**
 * 청약 라인(`site_type='subscription'`) 전용 단계군 — 판정회신_NS-1 §2 확정표.
 *
 * ⚠️ **subscription 으로 스코프한 것은 의도다.** 단계는 site_type 을 가로지르므로
 *    전역으로 걸면 다른 타입이 «조용히» 따라 움직인다 — 실측:
 *      redevelopment 51곳(접수 종료~입주 전 군) + 148곳(입주 후 군) = 199곳,
 *      landmark 120곳(「시세 · 분석」 → 「실거래가 · 시세」).
 *    §2 표의 머리가 「(subscription)」이라 그 범위를 그대로 지킨다. 넓히려면 그건 별도 판정이다.
 *
 * 군 분포 실측 2026-09-21 (합 2,826 = subscription 전수, ±0):
 *   접수 전·기간 내   59  (site_planning 1 · pre_announcement 51 · subscription_open 7) → 현행 유지
 *   접수 종료~입주 전 751 (award_pending 9 · award_announced 21 · construction 721)
 *   입주 후         2,016 (move_in_started 138 · post_move_in 1,878)
 */
const SUBSCRIPTION_STAGE_TAIL: Record<string, string> = {
  // ── 접수 종료 ~ 입주 전 — 공고 분양가·입주예정이 «확정» 인 구간.
  //    ⚠️ 「청약일정」을 더 약속하지 않는다. 접수는 끝났다.
  award_pending: '분양가 · 입주일정',
  award_announced: '분양가 · 입주일정',
  contract_signing: '분양가 · 입주일정',
  contract: '분양가 · 입주일정',
  construction: '분양가 · 입주일정',
  move_in_ready: '분양가 · 입주일정',   // 이름과 달리 「계약 종료」다 — 입주는 아직 미래(동군, 세분 발명 금지)
  pre_move_in: '분양가 · 입주일정',

  // ── 입주 후 — P4 기축 어휘 재사용(발명 0).
  move_in_started: '실거래가 · 시세',
  post_move_in: '실거래가 · 시세',
  landmark_active: '실거래가 · 시세',

  // ── 접수 전·기간 내는 «적지 않는다» → SITE_TYPE_TAIL 로 떨어져 현행 유지.
  //    site_planning · pre_announcement · model_house_open · special_supply · subscription_open
};

/**
 * 정비사업(`site_type='redevelopment'`) — 판정회신_NS-2 §3 ①.
 *
 * **입주 후 148곳만** 연다. 준공된 단지가 「재개발 · 진행현황」을 계속 다는 것은
 * subscription 96.9% 병의 재개발판이다 — 진행할 사업이 이미 끝났다.
 *
 * ⛔ 진행 중 정비(조합설립~착공)는 「재개발 · 진행현황」 «불변» 이다. 그게 맞는 말이다.
 * ⛔ 청약 국면·unsold 행은 «열지 않았다» — 조건 1쿼리 실측이 0 이었다:
 *      공고 보유(rcept) 0곳 · unsold_active 0곳 (합 0 < 10 → 백로그).
 *    청약 라인 단계에 있는 재개발 256곳은 있지만 «일정 데이터가 없다» —
 *    「청약일정」을 달면 없는 일정을 약속하는 것이 된다(NS-1 과 같은 병).
 */
const REDEV_STAGE_TAIL: Record<string, string> = {
  move_in_started: '실거래가 · 시세',
  post_move_in: '실거래가 · 시세',
  landmark_active: '실거래가 · 시세',
};

/**
 * 제목 꼬리 한 벌.
 * @param siteType   `apt_sites.site_type`
 * @param stage      `apt_sites.lifecycle_stage`
 */
/**
 * 「기축 계열」 — 제목이 **「실거래가 · 시세」를 약속하는 군**.
 *
 * 이 목록을 다른 파일에 다시 적지 않는다(관례 3 · 복제처 금지).
 * 위 두 표가 쓰는 것과 «같은 단계 집합» 이고, B-1 계열 가드도 이 술어를 재사용한다.
 */
export const BUILT_STAGES = ['move_in_started', 'post_move_in', 'landmark_active'] as const;

/** 제목이 실거래를 약속하는 현장인가. */
export function isBuiltLike(siteType?: string | null, stage?: string | null): boolean {
  return siteType === 'trade' || (BUILT_STAGES as readonly string[]).includes(String(stage ?? ''));
}

/* ════════════════════════════════════════════════════════════════════════════
 * CPL A-5 — 단계 판정부 (2026-09-23). **title 과 본문이 이 한 곳을 공유한다.**
 *
 * ── 왜 여기인가 ──
 * 위 두 꼬리표(`SUBSCRIPTION_STAGE_TAIL`·`REDEV_STAGE_TAIL`)는 사실 «단계군» 이다 —
 * 「끝난 청약을 더 약속하지 않는다」는 판정을 «문구» 로 적어 둔 것이다. 본문 배치도
 * 정확히 같은 판정을 필요로 한다(CPL v5 §A-5 단계 매트릭스). 표를 두 벌로 적으면
 * 제목은 「실거래가」라 말하는데 본문은 청약 블록을 그리는 날이 온다.
 * → 군을 먼저 세우고, 꼬리 문구는 그 군의 «소비처» 로 내린다.
 *
 * ⚠️ **titleTail 의 출력은 한 글자도 바뀌지 않았다.** 아래 두 표는 위 두 꼬리표와
 *    키 집합이 «정확히 같고», `titleTail` 이 site_type 스코프를 그대로 유지한다
 *    (실측 redevelopment 199곳·landmark 120곳이 조용히 따라 움직이지 않게 — NS-1 §2).
 * ⛔ 새 단계를 여기에 «발명하지» 않는다. 키는 `lifecycle-label.ts` 의 CHECK 허용값이다.
 * ════════════════════════════════════════════════════════════════════════════ */

/** CPL v5 §A-5 단계 매트릭스의 다섯 칸. 이 밖의 국면을 만들지 않는다. */
export type DetailStage =
  | 'pre_notice'   // 공고전
  | 'applying'     // 청약 기간 내
  | 'post_apply'   // 청약 후 ~ 입주 전
  | 'remaining'    // 잔여 · 선착순
  | 'moved_in';    // 입주 후

/**
 * 청약 라인(`site_type='subscription'`) 단계군.
 * ⚠️ `SUBSCRIPTION_STAGE_TAIL` 과 **같은 키 집합** 이다 — 한쪽만 늘리지 말 것.
 */
const SUBSCRIPTION_STAGE_GROUP: Record<string, DetailStage> = {
  site_planning: 'pre_notice',
  pre_announcement: 'pre_notice',

  model_house_open: 'applying',
  special_supply: 'applying',
  subscription_open: 'applying',

  award_pending: 'post_apply',
  award_announced: 'post_apply',
  contract_signing: 'post_apply',
  contract: 'post_apply',
  construction: 'post_apply',
  move_in_ready: 'post_apply',   // 이름과 달리 「계약 종료」다 — 입주는 아직 미래
  pre_move_in: 'post_apply',

  move_in_started: 'moved_in',
  post_move_in: 'moved_in',
  landmark_active: 'moved_in',
};

/**
 * 정비사업(`site_type='redevelopment'`) 단계군.
 *
 * ⚠️ **진행 5단계(조합설립~착공)는 «공고 전» 이다.** 같은 `construction` 이라도
 *    청약 라인의 것은 「공사 중(분양 끝남)」이고 정비 라인의 것은 「착공(분양 전)」이다.
 *    실측이 그 근거다 — 청약 국면에 있는 재개발 256곳에 «일정 데이터가 없다»(NS-2 §3).
 *    한 표로 합치면 착공한 조합에 「청약 결과」 블록을 세우게 된다.
 */
const REDEV_STAGE_GROUP: Record<string, DetailStage> = {
  union_established: 'pre_notice',
  constructor_selected: 'pre_notice',
  plan_approved: 'pre_notice',
  mgmt_approved: 'pre_notice',
  construction: 'pre_notice',
  site_planning: 'pre_notice',
  pre_announcement: 'pre_notice',

  move_in_started: 'moved_in',
  post_move_in: 'moved_in',
  landmark_active: 'moved_in',
};

/**
 * 단계 → 군. **표에 없으면 `null`** — 모르는 단계를 아무 칸에나 넣지 않는다.
 * ⚠️ `unsold_active` 는 여기서 다루지 않는다. 그 하나는 site_type 을 가로질러 이기므로
 *    소비처(`titleTail`·`deriveStage`)의 «첫 줄» 에서 걸러진다.
 */
export function stageGroupOf(siteType?: string | null, stage?: string | null): DetailStage | null {
  if (!stage) return null;
  const table = siteType === 'subscription' ? SUBSCRIPTION_STAGE_GROUP
              : siteType === 'redevelopment' ? REDEV_STAGE_GROUP
              : undefined;
  return table?.[stage] ?? null;
}

export interface DeriveStageInput {
  /** `apt_sites.site_type` (없으면 `siteTypeOf()` 의 폴백 사슬 결과). */
  siteType?: string | null;
  /** `apt_sites.lifecycle_stage`. */
  lifecycleStage?: string | null;
  /** `subStatusOf(sub, today)` 결과. 날짜가 말하는 «지금 접수 중인가». */
  subStatus?: 'open' | 'upcoming' | 'closed' | null;
  /** `moveInPast(입주월, today)` 결과 — `lib/apt/schedule.ts` 한 곳이 판정한다. */
  moveInPast?: boolean;
}

/**
 * **본문 배치용 단계 판정.** CPL v5 §A-5 매트릭스의 행을 고른다.
 *
 * `titleTail` 과 «같은 군 표» 를 쓰고, 그 위에 재료 둘을 더 본다:
 *   · `subStatus==='open'` — 접수 기간 «안» 이라는 것은 날짜가 말하는 사실이고,
 *     단계 컬럼보다 강하다(실측 `subscription_open` 은 전국 7곳뿐인데 실제 접수 중
 *     현장은 공고 날짜로만 알 수 있다).
 *   · `moveInPast` — 단계 컬럼이 «비어 있을 때만» 쓴다. 있는데 무시하면
 *     stage-derive 크론이 매일 갱신하는 축을 화면이 조용히 뒤집는 것이 된다.
 *
 * ⛔ **모르면 `null` 이다.** 호출부는 null 을 「입주 후가 아님」으로만 읽고 종전 배치를
 *    유지한다 — 모르는 현장의 블록을 추측으로 끄지 않는다(§7-1 계보).
 */
export function deriveStage(input: DeriveStageInput): DetailStage | null {
  const { siteType, lifecycleStage, subStatus, moveInPast } = input;

  // ① 잔여·선착순 — 단계 하나가 유일한 증거다(UNSOLD_TAIL 과 같은 규율).
  if (lifecycleStage === 'unsold_active') return 'remaining';
  // ② 입주 후 — site_type 을 가로지른다. 제목이 「실거래가」를 약속하는 군과 같은 집합이다.
  if ((BUILT_STAGES as readonly string[]).includes(String(lifecycleStage ?? ''))) return 'moved_in';
  // ③ 접수 기간 «안». 날짜가 단계 컬럼을 이긴다.
  if (subStatus === 'open') return 'applying';
  // ④ 라인별 군 표 — titleTail 과 같은 표다.
  const byTable = stageGroupOf(siteType, lifecycleStage);
  if (byTable) return byTable;
  // ⑤ 단계가 말이 없을 때의 site_type 바닥. 기축 계열 두 타입만 답이 확실하다.
  if (siteType === 'trade' || siteType === 'landmark') return 'moved_in';
  if (siteType === 'unsold') return 'remaining';
  // ⑥ 단계 컬럼이 «비어 있을 때만» 입주월을 본다.
  if (!lifecycleStage && moveInPast) return 'moved_in';
  return null;
}

export function titleTail(siteType?: string | null, stage?: string | null): string {
  if (stage === 'unsold_active') return UNSOLD_TAIL;
  /* ⚠️ 군은 공유하고 «문구» 만 여기서 고른다. site_type 스코프는 그대로다 —
         subscription 밖으로 새면 redevelopment 199곳·landmark 120곳이 따라 움직인다. */
  const group = stageGroupOf(siteType, stage);
  if (siteType === 'subscription') {
    if (group === 'post_apply') return SUBSCRIPTION_STAGE_TAIL.award_pending;
    if (group === 'moved_in') return SUBSCRIPTION_STAGE_TAIL.post_move_in;
  } else if (siteType === 'redevelopment') {
    if (group === 'moved_in') return REDEV_STAGE_TAIL.post_move_in;
  }
  return SITE_TYPE_TAIL[String(siteType ?? '')] || '부동산 정보';
}

/* ════════════════════════════════════════════════════════════════════════════
 * CPL A-6 — 제목의 «숫자» (2026-09-23 · 운영자가 라이브에서 잡았다).
 *
 * ── 무엇이 거짓이었나 ──
 * 제목은 `{이름} {꼬리}{숫자}` 로 조립되는데, 그 숫자는 «언제나»
 * `apt_sites.price_min/max` 였다. 그 열은 **분양가** 다 — `apt-price-sync` 가
 * 「⛔ 분양가 필드(price_min/max)에 실거래를 주입하지 않는다(E-8)」고 못 박은 바로 그 열이다.
 * 그런데 입주 후 단계의 꼬리는 「실거래가 · 시세」다.
 *
 *   실물 2026-09-23 — `/apt/1908` = `인천영종-60블록-공공분양주택`(post_move_in)
 *     「… 실거래가 · 시세 3.4억~3.9억」 ← 34,325~39,153만원 = **2022년 공고 분양가**
 *     같은 이름·같은 시도의 `apt_transactions` 행 **0건**.
 *   값이 틀린 게 아니라 «라벨이 거짓말» 이다 — 읽는 사람은 그 숫자를 지금 시세로 읽는다.
 *
 * ── 실측 (2026-09-23 · `is_active` · 입주 후 군 = BUILT_STAGES ∪ site_type='trade') ──
 *     군 전체                4,667곳
 *     분양가 보유            4,349곳 ← 전부 「시세」 라벨에 분양가를 달고 있었다
 *       그중 실거래 0건       1,360곳 ← 숫자를 «떼는» 자리
 *       그중 실거래 있음      2,989곳 ← 숫자를 «실거래로 가는» 자리
 *   ⚠️ 실거래가 있어도 두 축은 다른 수다 — `/apt/1914`(평택석정공원 화성파크드림)
 *      분양가 4.5억~5.9억 / 실거래 2.7억~4.8억. 「있으니 그대로 둬도 된다」가 아니다.
 *
 * ── 규칙 ──
 *   입주 후(`deriveStage()==='moved_in'`) : 실거래가 «있을 때만» 실거래 범위.
 *                                           없으면 숫자 없이 — 단계가 확정으로 아는 사실로 채운다.
 *   그 밖의 단계                           : 종전 그대로 분양가. 그 꼬리들은 분양가를 약속한다.
 *
 * ⛔ **새 판정식을 만들지 않았다.** 축은 A-5 의 `deriveStage()` 다섯 칸 하나다.
 * ⚠️ 호출부는 `deriveStage({siteType, lifecycleStage})` «두 재료만» 넘긴다 —
 *    꼬리(`titleTail`)가 보는 것이 정확히 그 둘이기 때문이다. `subStatus`·`moveInPast`
 *    까지 넣으면 제목의 «숫자» 가 제목의 «꼬리» 와 다른 근거로 움직인다.
 *    그 둘만 쓰면 대응이 «정확히» 맞는다(아래 실측):
 *      꼬리가 시세를 약속하는 집합 = 「BUILT_STAGES」 ∪ 「site_type ∈ {trade, landmark}」
 *      = `deriveStage` 의 ② ∪ ⑤ = `moved_in`.
 *      ⑤ 를 가로챌 수 있는 유일한 경로는 ①`unsold_active`(꼬리도 「잔여세대 · 선착순」이라
 *      시세를 약속하지 않는다)뿐이다 — 실측: trade·landmark 2,503곳 중 접수 기간 안 **0곳**.
 * ⛔ 「없으면 숫자 없이」가 제목을 «빈약하게» 두라는 뜻이 아니다. 세대수·입주월은 그 단계가
 *    확정으로 가진 사실이고, 실거래와 달리 라벨이 거짓이 되지 않는다.
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * 꼬리가 「시세·실거래가」를 약속했는가.
 *
 * ⚠️ **새 판정식이 아니다.** 이미 만들어진 꼬리에게 「방금 무엇을 약속했나」를 되묻는 것뿐이고,
 *    표를 다시 적지 않는다(관례 3 · 복제처 금지). 위 세 표의 문구를 전수로 훑어 잠근다
 *    (`detail-title-facts.test.ts` — 표가 늘어나면 거기서 걸린다).
 *
 * ── 왜 단계만으로는 부족한가 (실측으로 잡힌 자리) ──
 * `deriveStage` 의 `moved_in` 과 「시세를 약속하는 꼬리」는 «거의» 같지만 한 틈이 있다:
 * `lifecycle_stage ∈ BUILT_STAGES` 는 site_type 을 가로지르는데(②), 꼬리표는
 * subscription·redevelopment·trade·landmark 네 타입에만 열려 있다. 그래서
 * `site_type='unsold'` + `post_move_in` 은 단계가 `moved_in` 인데 꼬리는 「미분양 현황」이다 —
 * 거기서 분양가를 떼면 «약속하지도 않은 라벨» 때문에 사실을 잃는다.
 *   실측 2026-09-23: site_type='unsold' 213곳의 단계는 unsold_active 192 · NULL 20 ·
 *   site_planning 1 — 그 틈에 앉은 현장은 **0곳** 이다. 지금 출력은 어느 쪽이든 같다.
 * ⛔ 그래도 틈을 열어 두지 않는다. 규칙은 「라벨이 약속한 것만 숫자로 답한다」이고,
 *    두 소비처가 갈리는 날 조용히 사실을 잃는 쪽이 더 나쁘다.
 */
export function tailPromisesMarket(tail: string): boolean {
  return /시세|실거래가/.test(String(tail ?? ''));
}

export interface TitleFactsInput {
  /** `deriveStage()` 결과. **여기서 단계를 다시 판정하지 않는다.** */
  stage: DetailStage | null;
  /** `titleTail()` 이 «방금 낸» 꼬리. 숫자는 그 라벨이 약속한 것만 답한다. */
  tail?: string | null;
  /** `apt_sites.price_min` — «분양가» 다(만원). 실거래가 아니다. */
  priceMin?: number | null;
  /** `apt_sites.price_max` (만원). */
  priceMax?: number | null;
  /** `apt_sites.price_source`. `'synthetic'` 은 지역 채움값이라 사실이 아니다(synthetic-price.ts). */
  priceSource?: string | null;
  /** `apt_transactions.deal_amount` 묶음(만원). `fetchUnifiedData().trades` 를 그대로 넘긴다. */
  tradeAmounts?: ReadonlyArray<number | string | null | undefined>;
  /** 세대수. 제목·본문이 쓰는 «한 벌» 값을 그대로 받는다(page.tsx 의 `units`). */
  units?: number | null;
  /** 입주예정 `YYYYMM`. 판독은 `schedule.ts` 의 `ymText` 한 곳이 한다. */
  moveInYm?: string | null;
}

/** 0·음수·NaN·빈 문자열은 값이 아니다. */
const posAmount = (v: number | string | null | undefined): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * **실거래** 범위 표시(「2.7억~4.8억」). 거래가 한 건도 없으면 `null`.
 *
 * 산식은 본문 실거래 요약(`sections/TradeSummaryText.tsx`)과 같다 — 최소·최대 그대로다.
 * ⛔ 평균·평당가·추세를 여기서 «짓지» 않는다. 한 건뿐이면 범위가 아니라 한 값이다.
 */
export function tradeRangeText(amounts?: ReadonlyArray<number | string | null | undefined>): string | null {
  const ok = (amounts ?? []).map(posAmount).filter((n): n is number => n !== null);
  if (ok.length === 0) return null;
  const lo = Math.min(...ok);
  const hi = Math.max(...ok);
  return lo === hi ? fmtAmount(lo) : `${fmtAmount(lo)}~${fmtAmount(hi)}`;
}

/**
 * **분양가** 표시(「4.5억~」 — 바닥부터). 두 끝이 다 있어야 하고, 합성가면 `null`.
 *
 * 2026-10-24 Node 「분양가는 최고가보다 최저가로」 — 종전 「4.5억~5.9억」 범위를 하한 앞세움으로 바꿨다.
 * ⚠️ `price_min` 은 «가장 작은 타입의 최고층가» 일 수 있어 「최저」라 부르지 않는다 — 「~」 만 단다.
 * ⚠️ `style:'word'` 는 문장용(「4.5억부터」).
 *
 * ⚠️ 표기는 종전 `page.tsx` 인라인식과 «한 글자도» 같다(`fmtAmount` 가 같은 식이다).
 * ⚠️ 합성가 차단은 새로 건 것이다 — 실측 2026-09-23 `price_source='synthetic'` **0행**
 *    이라 오늘의 출력은 그대로고, 그 열이 다시 채워지는 날 조용히 거짓이 되지 않는다.
 */
export function supplyPriceText(
  priceMin?: number | null, priceMax?: number | null, priceSource?: string | null,
  style: 'tilde' | 'word' = 'tilde',
): string | null {
  if (priceSource === 'synthetic') return null;
  const min = posAmount(priceMin);
  const max = posAmount(priceMax);
  return min && max ? fmtPriceFrom(fmtAmount(Math.min(min, max)), fmtAmount(Math.max(min, max)), style) : null;
}

/**
 * 제목 꼬리 «뒤» 에 붙는 사실 조각. 앞에 공백을 포함해 돌려준다(없으면 빈 문자열).
 * 종전 `page.tsx` 의 `priceStr` 자리를 그대로 대체한다.
 */
export function titleFacts(i: TitleFactsInput): string {
  /* 「시세」 라벨은 입주 후 군에서만 붙고(NS-1 §2), 숫자는 그 라벨이 약속한 것만 답한다.
     ⚠️ `tail` 을 안 넘기면 단계만으로 판정한다 — 종전 호출부 계약을 깨지 않기 위해서다. */
  const marketLabel = i.tail == null ? true : tailPromisesMarket(i.tail);
  if (i.stage === 'moved_in' && marketLabel) {
    // 실거래가 «있을 때만» 숫자를 쓴다.
    const trade = tradeRangeText(i.tradeAmounts);
    if (trade) return ` ${trade}`;
    /* 실거래 0건 — 분양가를 시세 라벨에 «달지 않는다». 단계가 아는 사실로 바꿔 채운다.
       ⛔ 둘 다 없으면 꼬리만 남는다. 「시세 미정」 같은 자리표시를 만들지 않는다. */
    const units = posAmount(i.units);
    const moveIn = ymText(i.moveInYm);
    const facts = [
      units ? `${units.toLocaleString('ko-KR')}세대` : null,
      moveIn ? `${moveIn} 입주` : null,
    ].filter(Boolean);
    return facts.length > 0 ? ` ${facts.join(' · ')}` : '';
  }
  // 그 밖의 단계 — 꼬리가 분양가를 약속한다. 출력은 종전과 같다.
  const supply = supplyPriceText(i.priceMin, i.priceMax, i.priceSource);
  return supply ? ` ${supply}` : '';
}

/**
 * **description 의 첫 문장** — 「질의에 답하는 사실」 한 줄 (CPL v5 §A-6).
 *
 * ── 왜 정의문이 먼저면 안 되는가 ──
 * 종전 첫 문장은 `buildSiteOverview` 의 정의문이었다:
 *   「인천영종 A60블록 공공분양주택은(는) 인천 중구에 위치한 아파트 단지입니다.」
 * 이것은 «무엇인가» 에 답할 뿐, 사람이 실제로 던진 질의(「얼마냐」·「언제냐」)에 답하지 않는다.
 * AI 브리핑·음성 검색은 «첫 문장» 을 인용하므로, 그 자리에 답이 없으면 답이 없는 채로 인용된다.
 *
 * ⛔ **없는 답을 지어내지 않는다.** 재료가 없으면 `null` 이고, 호출부는 종전처럼
 *    정의문으로 시작한다 — 「가격 미정」 같은 자리표시를 첫 문장으로 세우지 않는다.
 * ⛔ 라벨은 제목과 «같은 규칙» 이다 — 입주 후에는 실거래만 「시세」라 부른다.
 *    그래서 입주 후 + 실거래 0건이면 분양가로 답하지 않고 `null` 을 낸다.
 */
export function answerFirstSentence(i: TitleFactsInput): string | null {
  if (i.stage === 'moved_in' && (i.tail == null || tailPromisesMarket(i.tail))) {
    const trade = tradeRangeText(i.tradeAmounts);
    return trade ? `최근 실거래가는 ${trade}입니다.` : null;
  }
  const supply = supplyPriceText(i.priceMin, i.priceMax, i.priceSource, 'word');
  return supply ? `분양가는 ${supply}입니다.` : null;
}

/* ── ⑨ 유형어 중복 방지 (Node 12:50, 2026-09-28) ──────────────────────────
 * 이름이 이미 유형어로 끝나면(괄호 별칭은 떼고 본다) 제목·og·JSON-LD·공유 제목에 유형어를 다시 붙이지 않는다.
 *   「서금사재정비촉진A구역 재개발」 + 「재개발 · 진행현황」 → 「서금사재정비촉진A구역 재개발 · 진행현황」
 *   「센텀1구역 재건축(재송코오롱아파트)」 + 「재개발」 → 「센텀1구역 재건축(재송코오롱아파트)」 */
// 정의는 cl-core/names 로 옮겼다(hc3 H6 단일 출처 · 동작 동일) — 기존 import 경로 보존을 위해 재수출.
export { nameEndsWithTypeWord, withTypeWord, withTitleTail, collapseTypeWordDup } from '@/lib/cl-core/names';
